import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import areaService from '../services/AreaService';
import { ADMIN_FORM_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const AdminCreateArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const stateColegioId = location.state?.idColegio || null;
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    idColegio: activeColegioId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del área.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando área...');
    setMessageType('info');
    try {
      await areaService.createArea({
        nombre: form.nombre,
        descripcion: form.descripcion,
        idColegio: form.idColegio
      });
      setMessage('Área creada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'areas' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el área.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="areas"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_FORM_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de área</h1>
            <p className="page-subtitle">Crearás una nueva área paral colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-sitemap" />
                <span>Datos del área</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos del área. Integraremos el backend cuando estén los endpoints disponibles.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Área X"
                  />
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción del área"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'areas' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear área</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateArea;
